import styled from 'styled-components';

import { IconCheckmark } from '@components/icons/checkmark';
import { IconDirectionDown } from '@components/icons/direction-down';
import { IconMove } from '@components/icons/move';
import { IconStages } from '@components/icons/stages';
import { Menu } from '@components/menu';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

type StageOption = { value: number; label: string };

type StageMenuProps = {
  value: number | null;
  options: StageOption[];
  onSelect: (stageId: number) => void;
  // Only the caller knows whether the row it sits in has width to give.
  isFullWidth?: boolean;
};

export const StageMenu = ({ value, options, onSelect, isFullWidth = false }: StageMenuProps) => {
  const { t } = useScopedTranslate(locale);
  const current = options.find((option) => option.value === value);

  return (
    <Menu
      placement="bottom-end"
      buttonProps={{
        variant: 'secondary',
        color: 'textPrimary',
        iconLeft: <IconStages />,
        iconRight: <IconDirectionDown />,
        text: current?.label,
        tooltip: current ? t('inStage', current.label) : undefined,
        style: isFullWidth ? { flexGrow: 1, minWidth: 0 } : { maxWidth: pxToRem(280) },
        textStyle: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minWidth: 0,
          // The label takes the slack, not the button, or the centred trio would
          // strand it away from its icon.
          ...(isFullWidth && { flexGrow: 1, textAlign: 'left' }),
        },
      }}
    >
      <Menu.Label
        icon={<IconMove color="elementPurple80" />}
        label={<LabelText>{t('move')}</LabelText>}
      />
      <Menu.Separator />
      {options.map((option) => {
        const isCurrent = option.value === value;
        return (
          <Menu.Item
            key={option.value}
            label={option.label}
            isActive={isCurrent}
            iconRight={isCurrent ? <IconCheckmark color="elementBlue100" /> : null}
            onSelect={isCurrent ? undefined : () => onSelect(option.value)}
          />
        );
      })}
    </Menu>
  );
};

const locale = {
  en: { move: 'Move to stage', inStage: 'Applicant is in {0}' },
  is: { move: 'Færa í dálk', inStage: 'Umsækjandi er í {0}' },
};

const LabelText = styled.span({
  textTransform: 'none',
});
